import React, { useMemo, useState, useEffect } from 'react'

import { useModal } from '@gousto-internal/citrus-react'
import actions from 'actions'
import { useDispatch, useSelector } from 'react-redux'
import { bindActionCreators } from 'redux'

import { boxSummaryDeliverySlotChosen } from 'actions/boxSummary'
import { loadOptimizelySDK } from 'actions/optimizely'
import { getIsAuthenticated } from 'selectors/auth'
import { getBasketOrderId, getBasketRecipesCount } from 'selectors/basket'
import { getIsCuratedBoxesEnabled, getIsLandOnMenuEnabled } from 'selectors/features'
import { getUserId } from 'selectors/user'
import { getTempDeliveryOptions } from 'utils/deliverySlotHelper'

import { useDoubleDeckerNav } from '../../../hooks/useDoubleDeckerNav'
import { checkQueryParams } from '../actions/menuRecipeDetails'
import { useBasket, useStock } from '../domains/basket'
import { useCurrentCollectionId } from '../domains/collections'
import { useMenu, useSetBrowserCTAVisibility } from '../domains/menu'
import fetchData from '../fetchData'
import { isMenuLoading, getMenuLoadingErrorMessage, getRecipeCount } from '../selectors/menu'
import { shouldShowCommunicationPanel, shouldShowCapacityInfo } from '../selectors/signupReduction'
import { CuratedBoxWarningModal, curatedBoxWarningModalName } from './CuratedBoxWarningModal'
import { useAddCuratedBoxRecipes } from './CuratedBoxWarningModal/useAddCuratedBoxRecipes'
import { MenuRecipesPage } from './MenuRecipesPage'
import { trackCuratedBoxLoseRecipesWarning } from './trackCuratedBox'
import { useHotjarIdentify } from './useHotjarIdentify'

const MenuRecipesPageWrapper = (ownProps) => {
  const {
    params,
    location: { query = {} },
  } = ownProps
  const [selectedCuratedBox, setSelectedCuratedBox] = useState(null)
  const dispatch = useDispatch()
  const { openModal } = useModal()
  const currentCollectionId = useCurrentCollectionId()
  const { addRecipe, canAddRecipes, menuId, numPortions } = useBasket()
  const { isPending } = useMenu()
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const getRecipeStock = useStock()
  const isDoubleDeckerFeatureOn = useDoubleDeckerNav()
  const [hasSeenWarningModal, setHasSeenWarningModal] = useState(false)
  const { addCuratedBoxRecipes } = useAddCuratedBoxRecipes(hasSeenWarningModal)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isCuratedBoxEnabled = useSelector(getIsCuratedBoxesEnabled) && !isAuthenticated
  const stateRecipeCount = useSelector(getRecipeCount)
  const showLoading = useSelector(isMenuLoading)
  const storeOrderId = useSelector(getBasketOrderId)
  const basketRecipesCount = useSelector(getBasketRecipesCount)
  const showCommunicationPanel = useSelector(shouldShowCommunicationPanel)
  const userId = useSelector(getUserId)
  const showCapacityInfo = useSelector(shouldShowCapacityInfo)
  const menuLoadingErrorMessage = useSelector(getMenuLoadingErrorMessage)

  const isLandOnMenuEnabled = useSelector(getIsLandOnMenuEnabled) && !isAuthenticated
  const { tempDate, tempSlotId } = useSelector(getTempDeliveryOptions)

  useEffect(() => {
    if (isLandOnMenuEnabled) {
      dispatch(boxSummaryDeliverySlotChosen({ date: tempDate, slotId: tempSlotId }))
    }
  }, [dispatch, isLandOnMenuEnabled, tempDate, tempSlotId])

  const actionDispatchers = useMemo(
    () =>
      bindActionCreators(
        {
          checkQueryParams,
          basketOrderLoaded: actions.basketOrderLoaded,
          portionSizeSelectedTracking: actions.portionSizeSelectedTracking,
          loadOptimizelySDK,
          fetchMenuData: fetchData,
        },
        dispatch,
      ),
    [dispatch],
  )

  const handleOnClickForCuratedBox = (
    curatedBoxRecipes,
    curatedBoxCollectionId,
    curatedBoxName,
    carouselIndex,
  ) => {
    if (!canAddRecipes) {
      setBrowserCTAVisible()

      return
    }

    setSelectedCuratedBox({
      curatedBoxRecipes,
      curatedBoxCollectionId,
      curatedBoxName,
      carouselIndex,
    })

    if (basketRecipesCount) {
      setHasSeenWarningModal(true)
      openModal(curatedBoxWarningModalName)
      dispatch(
        trackCuratedBoxLoseRecipesWarning(
          numPortions,
          curatedBoxName,
          carouselIndex,
          curatedBoxRecipes,
          curatedBoxCollectionId,
          menuId,
        ),
      )

      return
    }

    addCuratedBoxRecipes({
      curatedBoxRecipes,
      curatedBoxCollectionId,
      curatedBoxName,
      carouselIndex,
    })
  }

  useHotjarIdentify()

  return (
    <>
      <CuratedBoxWarningModal
        setSelectedCuratedBox={setSelectedCuratedBox}
        selectedCuratedBox={selectedCuratedBox}
        hasSeenWarningModal={hasSeenWarningModal}
      />
      <MenuRecipesPage
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...ownProps}
        stateRecipeCount={stateRecipeCount}
        menuCurrentCollectionId={currentCollectionId}
        showLoading={showLoading || isPending}
        orderId={params.orderId}
        storeOrderId={storeOrderId}
        numPortions={numPortions}
        showCommunicationPanel={showCommunicationPanel}
        userId={userId}
        shouldShowCapacityInfo={showCapacityInfo}
        menuLoadingErrorMessage={menuLoadingErrorMessage}
        query={query}
        addRecipe={addRecipe}
        curatedBoxesMethods={{
          getRecipeStock,
          isCuratedBoxEnabled,
        }}
        handleOnClickForCuratedBox={handleOnClickForCuratedBox}
        // dispatches below
        checkQueryParams={actionDispatchers.checkQueryParams}
        basketOrderLoaded={actionDispatchers.basketOrderLoaded}
        portionSizeSelectedTracking={actionDispatchers.portionSizeSelectedTracking}
        loadOptimizelySDK={actionDispatchers.loadOptimizelySDK}
        fetchMenuData={actionDispatchers.fetchMenuData}
        isDoubleDeckerFeatureOn={isDoubleDeckerFeatureOn}
      />
    </>
  )
}

export { MenuRecipesPageWrapper }
